import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { sortByName, matchesQuery } from "@/lib/listSort";

const LABELS = { last: "Last name A–Z", first: "First name A–Z", az: "A–Z", za: "Z–A", default: "Date order" };

// Search + sort state for a list; `fields(item)` returns searchable strings, `name(item)` the sort key.
export const useListControls = (items, { name = (x) => x.name, fields, initialSort = "az" } = {}) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(initialSort);
  const list = useMemo(() => {
    const f = fields || ((x) => [name(x)]);
    return sortByName((items || []).filter((x) => matchesQuery(query, ...f(x))), name, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, sort]);
  return { query, setQuery, sort, setSort, list };
};

// Convenience wrapper wired to useListControls' return value.
export const ListControlsBar = ({ ctl, modes = ["az", "za"], placeholder, testid, className = "", hideSearch = false }) => (
  <div className={className}>
    <ListSearchSort query={ctl.query} onQuery={ctl.setQuery} sort={ctl.sort} onSort={ctl.setSort} modes={modes} placeholder={placeholder} testid={testid} hideSearch={hideSearch} />
  </div>
);

// Search box + sort selector used across list views.
export const ListSearchSort = ({ query, onQuery, sort, onSort, modes = ["az", "za"], placeholder = "Search…", testid = "list", hideSearch = false }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-controls`}>
    {!hideSearch && <div className="relative flex-1 min-w-[180px]">
      <Search className="w-3.5 h-3.5 absolute top-1/2 -translate-y-1/2 left-3 text-[#525252]" />
      <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder={placeholder} data-testid={`${testid}-search`}
        className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none pl-9 pr-3 py-2 text-sm transition-colors" />
    </div>}
    <div className="inline-flex border border-[#262626]" role="radiogroup" aria-label="Sort" data-testid={`${testid}-sort`}>
      {modes.map((m) => (
        <button key={m} type="button" role="radio" aria-checked={sort === m} onClick={() => onSort(m)} data-testid={`${testid}-sort-${m}`}
          className={`px-3 py-2 text-[10px] uppercase tracking-wider transition-colors ${sort === m ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}>
          {LABELS[m]}
        </button>
      ))}
    </div>
  </div>
);
